import type React from 'react';
import { createContext, useContext, useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import {
  startConnection,
  stopConnection,
  onMessage,
  onConnectionStatus,
  setSignalRTokenProvider,
} from 'services';
import type { ConnectionStatus } from 'services';
import type { SignalRMessage } from 'types';
import { useAuthContext } from './AuthContext';
import { useStatusPanelMode } from './StatusPanelContext';
import { useWallAccess } from 'hooks/useWallAccess';

interface SignalRContextValue {
  connectionStatus: ConnectionStatus;
  lastCommunicationTime: Date | null;
  subscribe: (handler: (message: SignalRMessage) => void) => () => void;
}

const SignalRContext = createContext<SignalRContextValue | null>(null);

interface SignalRProviderProps {
  children: React.ReactNode;
}

/**
 * Provides SignalR connection management at the app level.
 * This ensures the connection persists across page/mode changes.
 */
export const SignalRProvider: React.FC<SignalRProviderProps> = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [lastCommunicationTime, setLastCommunicationTime] = useState<Date | null>(null);
  const [isTokenProviderReady, setIsTokenProviderReady] = useState(false);
  const handlersRef = useRef(new Set<(message: SignalRMessage) => void>());
  const { getAccessToken, isAuthenticated, isLoading: authLoading } = useAuthContext();
  const { isWallScreen } = useStatusPanelMode();
  const { isAllowed: wallAccessAllowed, isLoading: wallAccessLoading } = useWallAccess(isWallScreen);

  // Determine if we should connect:
  // - User is authenticated, OR
  // - Wall screen mode with IP whitelisted
  const shouldConnect = isAuthenticated || (isWallScreen && wallAccessAllowed);
  const accessCheckComplete = !authLoading && (!isWallScreen || !wallAccessLoading);

  // Set up token provider before any connection attempts (synchronous)
  useLayoutEffect(() => {
    setSignalRTokenProvider(getAccessToken);
    setIsTokenProviderReady(true);
  }, [getAccessToken]);

  // Track connection status changes and mark communication time when connected
  const handleConnectionStatus = useCallback((status: ConnectionStatus) => {
    setConnectionStatus(status);
    // Mark communication time when we successfully connect
    if (status === 'connected') {
      setLastCommunicationTime(new Date());
    }
  }, []);

  // Handle incoming messages and dispatch to all subscribers
  const handleMessage = useCallback((message: SignalRMessage) => {
    // Update last communication time on any message received
    setLastCommunicationTime(new Date());
    
    handlersRef.current.forEach((handler) => {
      try {
        handler(message);
      } catch (err) {
        console.error('SignalR message handler error:', err);
      }
    });
  }, []);

  // Subscribe to messages - returns unsubscribe function
  const subscribe = useCallback((handler: (message: SignalRMessage) => void) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  useEffect(() => {
    // Wait for token provider to be set and access check to complete
    if (!isTokenProviderReady || !accessCheckComplete) {
      return;
    }

    // Only connect if user has access
    if (!shouldConnect) {
      return;
    }

    // Start connection once at app level
    startConnection();

    // Subscribe to connection status changes
    const unsubStatus = onConnectionStatus(handleConnectionStatus);

    // Subscribe to messages and dispatch to registered handlers
    const unsubMessage = onMessage(handleMessage);

    return () => {
      unsubStatus();
      unsubMessage();
      // Stop connection when app unmounts (browser close/navigation away)
      stopConnection();
    };
  }, [handleMessage, handleConnectionStatus, isTokenProviderReady, shouldConnect, accessCheckComplete]);

  return (
    <SignalRContext.Provider value={{ connectionStatus, lastCommunicationTime, subscribe }}>
      {children}
    </SignalRContext.Provider>
  );
};

export function useSignalR(): SignalRContextValue {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context;
}
